import React, { useCallback, useEffect, useState } from 'react';
import {
  ATMField,
  ATMGrid,
  ATMHeader,
  ATMSegment,
  ATMSelect,
  ATMInput,
  ATMTextArea,
  ATMDivider,
} from 'shared-it-appmod-ui';
import LabelRequired from 'src/components/atoms/label-required/label-required.component';
import FormFlagUpload from 'src/components/organisms/form-flag-upload/form-flag-upload.component';
import FormFlag from 'src/components/molecules/form-flag/form-flag.component';
import { FileCategory } from 'src/constants';
import Lang from 'src/libraries/language';
import { IRequestFormMethods } from 'src/models/request.model';
import { useSensitiveCommunitiesContext } from 'src/contexts/sensitive-communities.context';
import { useDroneAreasContext } from 'src/contexts/drone-areas.context';
import { useSensitiveCitiesContext } from 'src/contexts/sensitive-cities.context';
import FlightRequestContactInformationForm from '../../flight-request-contact-information/flight-request-contact-information-form/flight-request-contact-information-form.component';
import FlightRequestConstructionLandingZoneForm from '../../flight-request-construction-landing-zone/flight-request-construction-landing-zone-form/flight-request-construction-landing-zone-form.component';
import FlightRequestConstructionStructure from '../../flight-request-construction-structure/flight-request-construction-structure.component';
import FlightRequestFlightArea from '../../flight-request-flight-area/flight-request-flight-area.component';
import styles from '../flight-request-construction-contractor.module.scss';

type IProps = IRequestFormMethods;

const FlightRequestConstructionContractorForm: React.FC<IProps> = ({
  control,
  errors,
}) => {
  const { state: sCommunitiesState, actions: sCommunitiesActions } = useSensitiveCommunitiesContext();
  const { state: droneAreasState, actions: droneAreasActions } = useDroneAreasContext();
  const { state: sCitiesState, actions: sCitiesActions } = useSensitiveCitiesContext();
  const [sCityPamLead, setSCityPamLead] = useState<string | null>('');
  const [sCommunityPamLead, setSCommunityPamLead] = useState<string | null>('');
  useEffect(() => {
    if (sCommunitiesActions && sCitiesActions && droneAreasActions) {
      sCommunitiesActions.listGET();
      sCitiesActions.listGET();
      droneAreasActions.listGET();
    }
  }, [sCommunitiesActions, sCitiesActions, droneAreasActions]);

  const handleChange = useCallback(
    (value, type) => {
      if (type === 'city') {
        if (!value) {
          setSCityPamLead(null);
        } else {
          const sCity = sCitiesState.list.find((v) => v.sensitiveCity === value);
          setSCityPamLead(sCity.pamLead);
        }
        control.setValue('constructionContractorFlight.sensitiveCity', value);
      } else if (type === 'drone') {
        control.setValue('constructionContractorFlight.droneArea', value);
      } else {
        if (!value) {
          setSCommunityPamLead(null);
        } else {
          const sCommunity = sCommunitiesState.list.find((v) => v.sensitiveCommunity === value);
          setSCommunityPamLead(sCommunity.sensitiveCommunityPamLead);
        }
        control.setValue('constructionContractorFlight.sensitiveCommunity', value);
      }
    },
    [control, sCitiesState, sCommunitiesState]
  );

  const [sensitiveCustomerFlag, setSensitiveCustomerFlag] = useState(control?.defaultValuesRef?.current?.constructionContractorFlight?.sensitiveFlag || false);
  const defaultSCityPamLead = sCitiesState.list.find((v) => v.sensitiveCity === control?.defaultValuesRef?.current?.constructionContractorFlight?.sensitiveCity)?.pamLead;
  const defaultSCommunityPamLead = sCommunitiesState.list.find((v) => v.sensitiveCommunity === control?.defaultValuesRef?.current?.constructionContractorFlight?.sensitiveCommunity)?.sensitiveCommunityPamLead;

  return (
    <ATMSegment color="purple" className={styles.wrapper}>
      <ATMHeader>{Lang.TTL_REQUEST_CONSTRUCTION_CONTRACTOR}</ATMHeader>

      <ATMGrid columns={3}>
        <ATMGrid.Column>
          <ATMField
            name="constructionContractorFlight.projectTitle"
            label={Lang.LBL_PROJECT}
            as={ATMInput}
            placeholder={Lang.LBL_ENTER}
            maxLength={50}
            control={control}
            error={errors.constructionContractorFlight?.projectTitle}
          />
        </ATMGrid.Column>
        <ATMGrid.Column>
          <ATMField
            name="constructionContractorFlight.circuit"
            label={<LabelRequired>{Lang.LBL_PIPELINE_NUMBER}</LabelRequired>}
            as={ATMInput}
            placeholder={Lang.LBL_ENTER}
            control={control}
            error={errors.constructionContractorFlight?.circuit}
          />
        </ATMGrid.Column>
        <ATMGrid.Column>
          <ATMField
            name="constructionContractorFlight.purpose"
            label={<LabelRequired>{Lang.LBL_PURPOSE}</LabelRequired>}
            as={ATMInput}
            placeholder={Lang.LBL_ENTER}
            maxLength={200}
            control={control}
            error={errors.constructionContractorFlight?.purpose}
          />
        </ATMGrid.Column>
        <ATMGrid.Column />
        <ATMGrid.Column />
      </ATMGrid>

      <FlightRequestConstructionStructure
        category="constructionContractorFlight"
        control={control}
        errors={errors}
      />

      <FlightRequestConstructionLandingZoneForm
        category="constructionContractorFlight"
        control={control}
        errors={errors}
      />

      <ATMHeader size="small" className="text-underline">
        {Lang.LBL_OTHER_INFORMATION}
      </ATMHeader>

      <ATMGrid columns={1}>
        <ATMGrid.Column>
          <FormFlag
            name="constructionContractorFlight.nightFlag"
            label={Lang.LBL_NIGHT_FLIGHT}
            control={control}
            errors={errors}
          />
        </ATMGrid.Column>

        <ATMGrid.Column>
          <FormFlagUpload
            name="constructionContractorFlight.sensitiveFlag"
            listName="constructionContractorFlight.sensitiveFiles"
            label={Lang.LBL_SENSITIVE_CUSTOMER}
            allowedTypes={[FileCategory.SensitiveCustomer]}
            control={control}
            errors={errors}
            setValue={setSensitiveCustomerFlag}
          />
        </ATMGrid.Column>
        {sensitiveCustomerFlag && (
  <ATMGrid columns={4}>
  <ATMGrid.Column>
        <ATMField
              name="constructionContractorFlight.sensitiveCity"
              label={Lang.LBL_SENSITIVE_CITY}
              placeholder={Lang.LBL_SELECT}
              as={ATMSelect}
              control={control}
              options={sCitiesState.list.map((item) => ({
                key: item.sensitiveCityId,
                value: item.sensitiveCity,
                text: item.sensitiveCity,
              }))}
              onChange={([_, { value }]) => {
                handleChange(value, 'city');
                return value;
              }}
              selectOnBlur={false}
              error={errors?.constructionContractorFlight?.sensitiveCity}
              loading={!sCitiesState.list.length}
              disabled={!sCitiesState.list.length}
              clearable
        />
        {!(sCityPamLead === null) && (sCityPamLead || defaultSCityPamLead) && <>
          <div>City PAM Lead:</div>
        <div>{sCityPamLead || defaultSCityPamLead}</div>
        </>}
 </ATMGrid.Column>

        <ATMGrid.Column>
<ATMField
              name="constructionContractorFlight.sensitiveCommunity"
              label={Lang.LBL_SENSITIVE_COMMUNITIES}
              placeholder={Lang.LBL_SELECT}
              as={ATMSelect}
              control={control}
              options={sCommunitiesState.list.map((item) => ({
                key: item.sensitiveCommunityId,
                value: item.sensitiveCommunity,
                text: item.sensitiveCommunity,
              }))}
              onChange={([_, { value }]) => {
                handleChange(value, 'community');
                return value;
              }}
              selectOnBlur={false}
              error={errors?.constructionContractorFlight?.sensitiveCommunity}
              loading={!sCommunitiesState.list.length}
              disabled={!sCommunitiesState.list.length}
              clearable
/>
{!(sCommunityPamLead === null) && (sCommunityPamLead || defaultSCommunityPamLead) && <>
  <div style={{ opacity: '80%' }}>Community PAM Lead:</div>
        <div>{sCommunityPamLead || defaultSCommunityPamLead}</div>
        </>}
 </ATMGrid.Column>

 <ATMGrid.Column>
        <ATMField
              name="constructionContractorFlight.droneArea"
              label={Lang.LBL_DRONE_AREA}
              placeholder={Lang.LBL_SELECT}
              as={ATMSelect}
              control={control}
              options={droneAreasState.list.map((item) => ({
                key: item.droneAreasId,
                value: item.SensitiveCityLocation,
                text: item.SensitiveCityLocation,
              }))}
              onChange={([_, { value }]) => {
                handleChange(value, 'drone');
                return value;
              }}
              selectOnBlur={false}
              error={errors?.constructionContractorFlight?.droneArea}
              loading={!droneAreasState.list.length}
              disabled={!droneAreasState.list.length}
              clearable
        />
 </ATMGrid.Column>
  </ATMGrid>)}
        <ATMGrid.Column>
          <FormFlagUpload
            name="constructionContractorFlight.culturalFlag"
            listName="constructionContractorFlight.culturalFiles"
            label={Lang.LBL_CULTURAL_RESTRICTION}
            allowedTypes={[FileCategory.EnvironmentalCulturalRestrictions]}
            control={control}
            errors={errors}
          />
        </ATMGrid.Column>
      </ATMGrid>

      <FlightRequestContactInformationForm
        category="constructionContractorFlight"
        control={control}
        errors={errors}
      />

      <ATMHeader size="small" className="text-underline">
        {Lang.TTL_PLAN_OPERATIONS}
      </ATMHeader>

      <ATMGrid columns={1}>
        <ATMGrid.Column>
          <FormFlagUpload
            name="constructionContractorFlight.liftFlag"
            listName="constructionContractorFlight.liftFiles"
            label={Lang.LBL_LIFT_PLAN}
            allowedTypes={[FileCategory.LiftPlan]}
            control={control}
            errors={errors}
          />
        </ATMGrid.Column>

        <ATMGrid.Column>
          <FormFlag
            name="constructionContractorFlight.complianceFlag"
            label={Lang.LBL_SDGE_AOM_REVIEW_OPERATOR_COMPLIANCE}
            control={control}
            errors={errors}
          />
        </ATMGrid.Column>

        <ATMGrid.Column>
          <FormFlagUpload
            name="constructionContractorFlight.congestedFlag"
            listName="constructionContractorFlight.congestedFiles"
            label={Lang.LBL_CONGESTED_AREA_PLAN}
            allowedTypes={[FileCategory.CongestedAreaPlan]}
            control={control}
            errors={errors}
          />
        </ATMGrid.Column>
      </ATMGrid>

      <ATMHeader size="small" className="text-underline">
        {Lang.TTL_WORK_SCOPE}
      </ATMHeader>

      <ATMGrid columns={1}>
        <ATMGrid.Column>
          <FormFlag
            name="constructionContractorFlight.lineFlag"
            label={Lang.LBL_LONG_LINE}
            control={control}
            errors={errors}
          />
        </ATMGrid.Column>
        <ATMGrid.Column>
          <FormFlag
            name="constructionContractorFlight.sockFlag"
            label={Lang.LBL_SOCK_LINE_PULL}
            control={control}
            errors={errors}
          />
        </ATMGrid.Column>
        <ATMGrid.Column>
          <FormFlag
            name="constructionContractorFlight.platformFlag"
            label={Lang.LBL_PLATFORM_WORK}
            control={control}
            errors={errors}
          />
        </ATMGrid.Column>
        <ATMGrid.Column>
          <FormFlag
            name="constructionContractorFlight.skidFlag"
            label={Lang.LBL_SKID_TRANSFER}
            control={control}
            errors={errors}
          />
        </ATMGrid.Column>
        <ATMGrid.Column>
          <FormFlagUpload
            name="constructionContractorFlight.hecFlag"
            listName="constructionContractorFlight.hecFiles"
            label={Lang.LBL_SHORT_HAUL_HEC}
            allowedTypes={[FileCategory.ShortHaulHEC]}
            control={control}
            errors={errors}
          />
        </ATMGrid.Column>
      </ATMGrid>

      <ATMHeader size="small" className="text-underline">
        {Lang.TTL_AIRCRAFT_INFORMATION}
      </ATMHeader>

      <ATMGrid columns={3}>
        <ATMGrid.Column>
          <ATMField
            name="constructionContractorFlight.aircraftType"
            label={<LabelRequired>{Lang.LBL_AIRCRAFT_TYPE}</LabelRequired>}
            as={ATMInput}
            placeholder={Lang.LBL_ENTER}
            control={control}
            error={errors.constructionContractorFlight?.aircraftType}
          />
        </ATMGrid.Column>
        <ATMGrid.Column>
          <ATMField
            name="constructionContractorFlight.tailNo"
            label={<LabelRequired>{Lang.LBL_TAIL_NUMBER}</LabelRequired>}
            as={ATMInput}
            placeholder={Lang.LBL_ENTER}
            control={control}
            error={errors.constructionContractorFlight?.tailNo}
          />
        </ATMGrid.Column>
      </ATMGrid>

      <ATMGrid columns={1}>
        <ATMGrid.Column>
          <FormFlag
            name="constructionContractorFlight.trackerFlag"
            label={Lang.LBL_SDGE_TRACKER}
            control={control}
            errors={errors}
          />
        </ATMGrid.Column>
      </ATMGrid>

      <FlightRequestFlightArea
        category="constructionContractorFlight"
        control={control}
        errors={errors}
      />
      <ATMDivider />

      <ATMHeader size="small" className="text-underline">
        {Lang.TTL_BRIEF_DESCRIPTION}
      </ATMHeader>

      <ATMField
        name="constructionContractorFlight.comments"
        label={Lang.LBL_DESCRIPTION_COMMENT}
        control={control}
        as={ATMTextArea}
        placeholder={Lang.LBL_ENTER}
      />
    </ATMSegment>
  );
};

export default FlightRequestConstructionContractorForm;
